import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUp } from "lucide-react";
import { audienceSites } from "../audience/audienceSites";

export const AUDIENCES = [
  { id: "scientist", label: "Biotech operator", reply: "I am a biotech operator" },
  { id: "investor", label: "Investor", reply: "I am an investor" },
  { id: "treatment", label: "Looking for treatments", reply: "I am looking for treatments" },
];

export const SEND_DURATION = 620;

// A local role shortcut, not a chatbot classifier. Unrecognized replies stay here.
const roleAliases = {
  scientist: new Set(["biotech operator", "biotech", "science", "scientist", "researcher", "biologist", "molecular biologist", "computational biologist", "geneticist", "lab scientist", "lab automation", "pharma", "biotech founder", "work in science", "work in biotech", "work in science or biotech"]),
  investor: new Set(["investor", "investing", "venture capitalist", "vc", "angel investor", "biotech investor"]),
  treatment: new Set(["patient", "caregiver", "carer", "advocate", "family member", "parent", "looking for treatment", "looking for treatments", "looking for a treatment", "seeking treatment", "seeking a treatment", "seeking treatments"]),
};

export function resolveAudience(reply) {
  const role = reply.toLowerCase().replace(/[’‘]/g, "'").trim()
    .replace(/[.!?]+$/, "").replace(/\s+/g, " ")
    .replace(/^i(?: am|'m)\s+/, "").replace(/^(?:a|an)\s+/, "");
  return Object.keys(roleAliases).find((id) => roleAliases[id].has(role)) || null;
}

const crew = [
  { image: "researcher", className: "welcome-avatar-researcher" },
  { image: "principal-scientist", className: "welcome-avatar-principal" },
  { image: "senior-scientist", className: "welcome-avatar-senior" },
  { image: "computational-scientist", className: "welcome-avatar-computational" },
];

export default function AudienceWelcome() {
  const navigate = useNavigate();
  const [reply, setReply] = useState("I am ");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(null);
  const inputRef = useRef(null);
  const timerRef = useRef(null);
  const submittingRef = useRef(false);
  const audience = resolveAudience(reply);

  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  function selectReply(option) {
    if (submittingRef.current) return;
    setReply(option.reply);
    setError("");
    if (window.matchMedia?.("(hover: hover) and (pointer: fine)").matches) {
      inputRef.current?.focus({ preventScroll: true });
    }
  }

  function sendReply(event) {
    event.preventDefault();
    if (submittingRef.current) return;
    if (!audience) {
      setError("Choose the suggestion that fits you best.");
      return;
    }
    submittingRef.current = true;
    setError("");
    inputRef.current?.blur();
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      navigate(audienceSites[audience].base);
      return;
    }
    setSending(audience);
    timerRef.current = window.setTimeout(() => navigate(audienceSites[audience].base), SEND_DURATION);
  }

  return (
    <section className="audience-welcome journey-shell" aria-labelledby="welcome-question" data-sending={sending ? "true" : undefined} style={{ "--send-duration": `${SEND_DURATION}ms` }}>
      <div className="welcome-crew" aria-hidden="true">
        <span className="welcome-aside welcome-aside-left">Hey there.</span>
        {crew.map(({ image, className }, index) => (
          <div className={`welcome-avatar ${className}`} key={image} style={{ "--arrival": `${index * 90}ms` }}>
            <img src={`/images/discovery/${image}.png`} alt="" width="160" height="160" />
          </div>
        ))}
      </div>
      <div className="welcome-question-bubble">
        <p className="welcome-speaker"><img src="/favico.svg" width="16" height="16" alt="" />Cornucopia</p>
        <h1 id="welcome-question">Who are <em>you?</em></h1>
      </div>
      <form className="welcome-reply" aria-label="Choose your starting point" onSubmit={sendReply}>
        <div className="welcome-input-shell">
          <input
            ref={inputRef}
            aria-label="Your reply"
            aria-invalid={Boolean(error)}
            aria-describedby={error ? "welcome-reply-error" : undefined}
            value={reply}
            placeholder="I am…"
            maxLength={120}
            autoComplete="off"
            autoCapitalize="sentences"
            enterKeyHint="send"
            readOnly={Boolean(sending)}
            onChange={(event) => { setReply(event.target.value); setError(""); }}
            onKeyDown={(event) => {
              if (event.key === "Enter" && (event.nativeEvent.isComposing || event.keyCode === 229)) event.preventDefault();
            }}
          />
          <button className="welcome-send" type="submit" aria-label="Send reply" disabled={Boolean(sending)}>
            <ArrowUp size={24} strokeWidth={2} aria-hidden="true" />
          </button>
        </div>
        <div className="welcome-suggestions" role="group" aria-label="Suggested replies">
          {AUDIENCES.map((option) => (
            <button
              key={option.id}
              type="button"
              aria-pressed={audience === option.id}
              disabled={Boolean(sending)}
              onPointerDown={(event) => {
                // Keep the native keyboard if already typing; don't summon it on arrival.
                if (document.activeElement === inputRef.current) event.preventDefault();
              }}
              onClick={() => selectReply(option)}
            >{option.label}</button>
          ))}
        </div>
        {error && <p id="welcome-reply-error" className="welcome-reply-error" role="alert">{error}</p>}
        {sending && <div className="welcome-sent" aria-hidden="true">{reply.trim()}</div>}
        <span className="sr-only" role="status">{sending ? `Opening ${audienceSites[sending].label}…` : ""}</span>
      </form>
    </section>
  );
}
